import { useEffect, useState } from 'react';
import api from '../api';

/**
 * BD-04 — which BD Assistant steps the signed-in person has switched on or off
 * (GET /api/bd-assistant/me -> settings.steps), so a tab's empty state can say
 * "this step is switched off for you" instead of leaving the person to wonder
 * why nothing is there.
 *
 * `null` while loading or if the call fails: the caller then says nothing about
 * the switch rather than guessing.
 */
export type StepSwitches = Record<string, boolean> | null;

export function useBdStepSwitches(): StepSwitches {
  const [steps, setSteps] = useState<StepSwitches>(null);

  useEffect(() => {
    let active = true;
    api.get('/api/bd-assistant/me')
      .then((res) => {
        const s = res.data?.settings?.steps;
        if (active) setSteps(s && typeof s === 'object' ? s : null);
      })
      .catch(() => { if (active) setSteps(null); });
    return () => { active = false; };
  }, []);

  return steps;
}

const OFF_NOTE = ' This step is switched off for you. You can turn it on in Settings → BD Assistant.';

/** The sentence to append to a tab's empty state; empty unless the step is explicitly off. */
export const stepOffNote = (steps: StepSwitches, key: string): string => (steps && steps[key] === false ? OFF_NOTE : '');
